import React, { useState, useEffect, useRef } from 'react';
import { getParcelles, createParcelle, updateParcelle, deleteParcelle, getProjets, getParametrage, searchMembresForParcelle } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiUser, FiPhone, FiMail, FiMapPin, FiChevronUp, FiChevronDown } from 'react-icons/fi';

const Parcelles = () => {
  const [parcelles, setParcelles] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [projets, setProjets] = useState<any[]>([]);
  const [directions, setDirections] = useState<any[]>([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ numero: '', superficie: '', lot: '', nicad: '', numtf: '', projet_id: '', user_id: '', description: '', attribuable: true as boolean });

  // Filters
  const [filterProjet, setFilterProjet] = useState('');
  const [filterAttribution, setFilterAttribution] = useState('');
  const [filterDirection, setFilterDirection] = useState('');
  const [supMin, setSupMin] = useState('');
  const [supMax, setSupMax] = useState('');

  // Sort
  const [sortBy, setSortBy] = useState('numero');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  // Attributaire popup
  const [showAttribPopup, setShowAttribPopup] = useState(false);
  const [attribData, setAttribData] = useState<any>(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  // Autocomplete membre
  const [membreSearch, setMembreSearch] = useState('');
  const [membreResults, setMembreResults] = useState<any[]>([]);
  const [selectedMembre, setSelectedMembre] = useState<any>(null);
  const [showMembreDropdown, setShowMembreDropdown] = useState(false);
  const [membresLoading, setMembresLoading] = useState(false);
  const membreInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<any>(null);

  const loadData = () => {
    const params: any = { page, limit: 50, q: search, sort_by: sortBy, sort_order: sortOrder };
    if (filterProjet) params.projet_id = filterProjet;
    if (filterAttribution) params.attribution = filterAttribution;
    if (filterDirection) params.direction_id = filterDirection;
    if (supMin) params.sup_min = supMin;
    if (supMax) params.sup_max = supMax;

    getParcelles(params).then(r => {
      setParcelles(r.data.data);
      setTotal(r.data.total);
      setTotalPages(r.data.totalPages);
    }).catch(() => {});
  };

  useEffect(() => { loadData(); }, [page, search, filterProjet, filterAttribution, filterDirection, supMin, supMax, sortBy, sortOrder]);
  useEffect(() => {
    getProjets().then(r => setProjets(r.data)).catch(() => {});
    getParametrage('directions').then(r => setDirections(r.data)).catch(() => {});
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowMembreDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const resetForm = () => {
    setForm({ numero: '', superficie: '', lot: '', nicad: '', numtf: '', projet_id: '', user_id: '', description: '', attribuable: true });
    setSelectedMembre(null);
    setMembreSearch('');
    setMembreResults([]);
  };

  const openAdd = () => { resetForm(); setEditingId(''); setShowModal(true); };
  const openEdit = (p: any) => {
    setForm({ numero: p.numero || '', superficie: p.superficie || '', lot: p.lot || '', nicad: p.nicad || '', numtf: p.numtf || '', projet_id: p.projet_id || '', user_id: p.user_id || '', description: p.description || '', attribuable: p.attribuable !== 0 });
    setEditingId(p.id);
    if (p.user_id && p.attributaire_nom) {
      setSelectedMembre({ id: p.user_id, prenomnom: p.attributaire_nom, matricule: p.cni || '' });
      setMembreSearch(p.attributaire_nom);
    } else {
      setSelectedMembre(null);
      setMembreSearch('');
    }
    setShowModal(true);
  };

  // Autocomplete search
  const handleMembreSearchChange = (val: string) => {
    setMembreSearch(val);
    if (selectedMembre) {
      setSelectedMembre(null);
      setForm(f => ({ ...f, user_id: '' }));
    }

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (val.trim().length < 2) {
      setMembreResults([]);
      setShowMembreDropdown(false);
      return;
    }

    searchTimeout.current = setTimeout(() => {
      setMembresLoading(true);
      searchMembresForParcelle(val.trim())
        .then(r => {
          setMembreResults(Array.isArray(r.data) ? r.data : []);
          setShowMembreDropdown(true);
        })
        .catch(() => { setMembreResults([]); })
        .finally(() => setMembresLoading(false));
    }, 300);
  };

  const selectMembre = (m: any) => {
    setSelectedMembre(m);
    setMembreSearch(m.prenomnom);
    setForm(f => ({ ...f, user_id: m.id }));
    setShowMembreDropdown(false);
  };

  const clearMembre = () => {
    setSelectedMembre(null);
    setMembreSearch('');
    setForm(f => ({ ...f, user_id: '' }));
    setMembreResults([]);
    membreInputRef.current?.focus();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...form, superficie: form.superficie ? Number(form.superficie) : null, attribuable: form.attribuable ? 1 : 0, user_id: form.attribuable ? form.user_id : null };
      if (editingId) {
        await updateParcelle(editingId, payload);
        setSuccess('Parcelle mise à jour');
      } else {
        await createParcelle(payload);
        setSuccess('Parcelle créée avec succès');
      }
      setShowModal(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer cette parcelle ?')) return;
    await deleteParcelle(id);
    loadData();
  };

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(col);
      setSortOrder(col === 'superficie' ? 'DESC' : 'ASC');
    }
    setPage(1);
  };

  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return <span className="sort-icon inactive"><FiChevronUp style={{ opacity: 0.3, fontSize: 12 }} /></span>;
    return <span className="sort-icon active">{sortOrder === 'ASC' ? <FiChevronUp /> : <FiChevronDown />}</span>;
  };

  const clearFilters = () => {
    setSearch('');
    setFilterProjet('');
    setFilterAttribution('');
    setFilterDirection('');
    setSupMin('');
    setSupMax('');
    setSortBy('numero');
    setSortOrder('ASC');
    setPage(1);
  };

  const hasFilters = search || filterProjet || filterAttribution || filterDirection || supMin || supMax;

  return (
    <div>
      <h1 className="page-title">Liste des parcelles</h1>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="card-header">
          <span>Parcelles enregistrées ({total.toLocaleString('fr-FR')})</span>
          <button className="btn btn-success" onClick={openAdd}><FiPlus /> Nouvelle parcelle</button>
        </div>
        <div className="card-body">
          {/* Filters bar */}
          <div className="filters-bar">
            <div className="search-box" style={{ minWidth: 180 }}>
              <FiSearch className="search-icon" />
              <input type="text" className="form-input" placeholder="Rechercher..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-input filter-select" value={filterProjet} onChange={e => { setFilterProjet(e.target.value); setPage(1); }}>
              <option value="">Tous les projets</option>
              {projets.map((pr: any) => <option key={pr.id} value={pr.id}>{pr.libelle || pr.nom}</option>)}
            </select>
            <select className="form-input filter-select" value={filterAttribution} onChange={e => { setFilterAttribution(e.target.value); setPage(1); }}>
              <option value="">Tous les statuts</option>
              <option value="attribuee">Attribuée</option>
              <option value="non_attribuee">Non attribuée</option>
              <option value="non_attribuable">Non attribuable</option>
            </select>
            <select className="form-input filter-select" value={filterDirection} onChange={e => { setFilterDirection(e.target.value); setPage(1); }}>
              <option value="">Toutes les directions</option>
              {directions.map((d: any) => <option key={d.id} value={d.id}>{d.libelle}</option>)}
            </select>
            <div className="filter-date-group">
              <input type="number" className="form-input filter-date" value={supMin} onChange={e => { setSupMin(e.target.value); setPage(1); }} placeholder="Sup. min (m²)" title="Superficie minimum" min="0" />
              <span className="filter-date-sep">—</span>
              <input type="number" className="form-input filter-date" value={supMax} onChange={e => { setSupMax(e.target.value); setPage(1); }} placeholder="Sup. max (m²)" title="Superficie maximum" min="0" />
            </div>
            {hasFilters && (
              <button className="btn btn-outline btn-sm" onClick={clearFilters} title="Effacer les filtres">
                <FiX /> Effacer
              </button>
            )}
          </div>

          <div className="table-responsive">
            <table className="data-table sortable-table">
              <thead>
                <tr>
                  <th className="sortable-th" onClick={() => handleSort('numero')}>Numéro <SortIcon col="numero" /></th>
                  <th className="sortable-th" onClick={() => handleSort('projet_libelle')}>Projet <SortIcon col="projet_libelle" /></th>
                  <th className="sortable-th" onClick={() => handleSort('superficie')}>Superficie (m²) <SortIcon col="superficie" /></th>
                  <th className="sortable-th" onClick={() => handleSort('lot')}>Lot <SortIcon col="lot" /></th>
                  <th className="sortable-th" onClick={() => handleSort('nicad')}>NICAD <SortIcon col="nicad" /></th>
                  <th className="sortable-th" onClick={() => handleSort('attributaire_nom')}>Attributaire <SortIcon col="attributaire_nom" /></th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {parcelles.map(p => (
                  <tr key={p.id}>
                    <td>{p.numero || '-'}</td>
                    <td>{p.projet_libelle || p.projet || '-'}</td>
                    <td>{p.superficie ? Number(p.superficie).toLocaleString('fr-FR') : '-'}</td>
                    <td>{p.lot || '-'}</td>
                    <td>{p.nicad || '-'}</td>
                    <td>
                      {p.attribuable === 0 ? (
                        <span className="badge badge-orange">Non attribuable</span>
                      ) : p.attributaire_nom && p.user_id ? (
                        <span
                          className="attributaire-link"
                          onClick={(e) => {
                            setAttribData(p);
                            const rect = (e.target as HTMLElement).getBoundingClientRect();
                            setPopupPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
                            setShowAttribPopup(true);
                          }}
                        >
                          <FiUser style={{ marginRight: 4, verticalAlign: 'middle', fontSize: 13 }} />
                          {p.attributaire_nom}
                        </span>
                      ) : (
                        <span className="badge badge-gray">Non attribuée</span>
                      )}
                    </td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon btn-warning" onClick={() => openEdit(p)}><FiEdit2 /></button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(p.id)}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {parcelles.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Aucune parcelle trouvée</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              {page > 1 && <button onClick={() => setPage(page - 1)}>&laquo;</button>}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, page - 2) + i;
                if (p > totalPages) return null;
                return <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>;
              })}
              {page < totalPages && <button onClick={() => setPage(page + 1)}>&raquo;</button>}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? 'Modifier la parcelle' : 'Nouvelle parcelle'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group"><label>Numéro</label><input className="form-input" value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} required /></div>
                  <div className="form-group"><label>Superficie (m²)</label><input type="number" className="form-input" value={form.superficie} onChange={e => setForm({ ...form, superficie: e.target.value })} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Lot</label><input className="form-input" value={form.lot} onChange={e => setForm({ ...form, lot: e.target.value })} /></div>
                  <div className="form-group"><label>NICAD</label><input className="form-input" value={form.nicad} onChange={e => setForm({ ...form, nicad: e.target.value })} /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Numéro TF</label><input className="form-input" value={form.numtf} onChange={e => setForm({ ...form, numtf: e.target.value })} /></div>
                  <div className="form-group"><label>Projet</label>
                    <select className="form-input" value={form.projet_id} onChange={e => setForm({ ...form, projet_id: e.target.value })}>
                      <option value="">--</option>
                      {projets.map((pr: any) => <option key={pr.id} value={pr.id}>{pr.libelle || pr.nom}</option>)}
                    </select>
                  </div>
                </div>

                {/* Attribuable toggle */}
                <div className="form-group">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={form.attribuable}
                      onChange={e => {
                        const val = e.target.checked;
                        setForm(f => ({ ...f, attribuable: val, user_id: val ? f.user_id : '' }));
                        if (!val) { setSelectedMembre(null); setMembreSearch(''); }
                      }}
                    />
                    <span className="toggle-switch" />
                    <span>{form.attribuable ? 'Attribuable' : 'Non attribuable (espace vert, voirie...)'}</span>
                  </label>
                </div>

                {/* Attributaire - Autocomplete */}
                {form.attribuable && (
                <div className="form-group">
                  <label><FiUser style={{ verticalAlign: 'middle', marginRight: 4 }} /> Attributaire (membre)</label>
                  <div className="autocomplete-wrapper" ref={dropdownRef}>
                    <div className="autocomplete-input-wrapper">
                      <FiSearch className="autocomplete-search-icon" />
                      <input
                        ref={membreInputRef}
                        type="text"
                        className={`form-input autocomplete-input ${selectedMembre ? 'has-value' : ''}`}
                        placeholder="Rechercher par nom, matricule, email..."
                        value={membreSearch}
                        onChange={e => handleMembreSearchChange(e.target.value)}
                        onFocus={() => { if (membreResults.length > 0 && !selectedMembre) setShowMembreDropdown(true); }}
                        autoComplete="off"
                      />
                      {selectedMembre && (
                        <button type="button" className="autocomplete-clear" onClick={clearMembre}><FiX /></button>
                      )}
                    </div>

                    {/* Selected membre info */}
                    {selectedMembre && (
                      <div className="autocomplete-selected">
                        <FiUser />
                        <div>
                          <strong>{selectedMembre.prenomnom}</strong>
                          <span>
                            {selectedMembre.matricule && `Mat: ${selectedMembre.matricule}`}
                            {selectedMembre.direction && ` • ${selectedMembre.direction}`}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Dropdown results */}
                    {showMembreDropdown && (
                      <div className="autocomplete-dropdown">
                        {membresLoading && (
                          <div className="autocomplete-item loading">Recherche en cours...</div>
                        )}
                        {!membresLoading && membreResults.length === 0 && membreSearch.length >= 2 && (
                          <div className="autocomplete-item loading">Aucun membre trouvé</div>
                        )}
                        {membreResults.map(m => (
                          <div key={m.id} className="autocomplete-item" onClick={() => selectMembre(m)}>
                            <div className="autocomplete-item-main">
                              <strong>{m.prenomnom}</strong>
                              {m.direction && <span className="autocomplete-item-badge">{m.direction}</span>}
                            </div>
                            <div className="autocomplete-item-sub">
                              {m.matricule && <span>Mat: {m.matricule}</span>}
                              {m.email && <span>{m.email}</span>}
                              {m.telephone && <span>{m.telephone}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                )}

                <div className="form-group"><label>Description</label><textarea className="form-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Attributaire Popup */}
      {showAttribPopup && attribData && (
        <>
          <div className="popup-overlay" onClick={() => setShowAttribPopup(false)} />
          <div className="attrib-popup" style={{ top: popupPos.top, left: popupPos.left }}>
            <div className="attrib-popup-header">
              <FiUser />
              <strong>{attribData.attributaire_nom}</strong>
              <button className="attrib-popup-close" onClick={() => setShowAttribPopup(false)}><FiX /></button>
            </div>
            <div className="attrib-popup-body">
              {attribData.attributaire_matricule && (
                <div className="attrib-popup-row">
                  <span className="attrib-popup-label">Matricule</span>
                  <span>{attribData.attributaire_matricule}</span>
                </div>
              )}
              {attribData.attributaire_cni && (
                <div className="attrib-popup-row">
                  <span className="attrib-popup-label">CNI</span>
                  <span>{attribData.attributaire_cni}</span>
                </div>
              )}
              {attribData.attributaire_direction && (
                <div className="attrib-popup-row">
                  <FiMapPin className="attrib-popup-icon" />
                  <span>{attribData.attributaire_direction}{attribData.attributaire_service ? ` / ${attribData.attributaire_service}` : ''}</span>
                </div>
              )}
              {attribData.attributaire_telephone && (
                <div className="attrib-popup-row">
                  <FiPhone className="attrib-popup-icon" />
                  <span>{attribData.attributaire_telephone}</span>
                </div>
              )}
              {attribData.attributaire_email && (
                <div className="attrib-popup-row">
                  <FiMail className="attrib-popup-icon" />
                  <span>{attribData.attributaire_email}</span>
                </div>
              )}
              {attribData.attributaire_sexe && (
                <div className="attrib-popup-row">
                  <span className="attrib-popup-label">Sexe</span>
                  <span>{attribData.attributaire_sexe === 'M' ? 'Masculin' : 'Féminin'}</span>
                </div>
              )}
            </div>
            <div className="attrib-popup-footer">
              <a href={`/admin/membres/${attribData.user_id}`} className="btn btn-primary btn-sm">
                Voir le profil complet
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Parcelles;
